import { CheckCircleOutlined, DownloadOutlined, LoadingOutlined } from '@ant-design/icons'
import { Button, message } from 'antd'
import React, { useState } from 'react'
import { usePWAContext } from '../contexts/PWAContext'

interface PWAInstallButtonProps {
	size?: 'small' | 'middle' | 'large'
	block?: boolean
	className?: string
	onInstallSuccess?: () => void
	onInstallDismissed?: () => void
	onOpenApp?: () => void
	showManualInstructions?: boolean
}

const PWAInstallButton: React.FC<PWAInstallButtonProps> = ({ size = 'large', block = true, className = '', onInstallSuccess, onInstallDismissed }) => {
	const { isInstallable, isInstalling, isInstalled, isReady, triggerInstall } = usePWAContext()
	const [showSuccess, setShowSuccess] = useState(false)

	const handleInstallClick = async () => {
		try {
			const success = await triggerInstall()

			if (success) {
				// Show success state briefly
				setShowSuccess(true)
				message.success('WageNow app installed successfully!')
				onInstallSuccess?.()

				// Reset success state after animation
				setTimeout(() => setShowSuccess(false), 2000)
			} else {
				// User dismissed the installation
				message.info('Installation cancelled')
				onInstallDismissed?.()
			}
		} catch (error) {
			message.error('Installation failed. Please try again.')
		}
	}

	// Wait for PWA hook to be ready before making render decisions
	if (!isReady) {
		return (
			<Button type='primary' shape='round' size={size} block={block} loading={true} disabled={true} className={className}>
				Checking installation status...
			</Button>
		)
	}

	// Only render if installable - revert to conditional rendering
	if (!isInstallable || isInstalled) {
		return null
	}

	const getButtonIcon = () => {
		if (showSuccess) return <CheckCircleOutlined />
		if (isInstalling) return <LoadingOutlined spin />
		return <DownloadOutlined />
	}

	const getButtonText = () => {
		if (showSuccess) return 'App Installed!'
		if (isInstalling) return 'Installing...'
		return 'Install WageNow App'
	}

	const getButtonType = () => {
		if (showSuccess) return 'default' as const
		return 'primary' as const
	}

	return (
		<Button
			type={getButtonType()}
			shape='round'
			size={size}
			block={block}
			icon={getButtonIcon()}
			onClick={handleInstallClick}
			loading={isInstalling}
			disabled={isInstalling || showSuccess}
			className={className}
		>
			{getButtonText()}
		</Button>
	)
}

export default PWAInstallButton
